/**
 * TTS 播放状态机（T10）。
 *
 * 四态：`idle → loading → playing → idle`；任何一步失败进入 `error`（携带结构化
 * `VoiceIssue`），失败后可通过 `dismiss()` 回到 `idle`，或再次 `play()` 重试。
 *
 * 与 `src/core/tts` 的契约：调用 `adapter.speak({ text, signal })` 拿到整段音频
 * `{ audio, mime }`，再交给注入的音频元素播放。取消 / 失败绝不返回半截音频。
 *
 * 设计为纯类（不依赖 React），便于直接用 fake 适配器 + fake 音频元素做单测：
 * 测试不联网、不启动真实音频设备。
 */

import type { TtsAdapter, TtsRequest } from '../../core/tts';
import { createConfigIssue, toVoiceIssue } from './issues';
import type { AudioFactory, ObjectUrlAdapter, TtsPlaybackState, VoiceIssue } from './types';

export interface TtsPlayerSnapshot {
  state: TtsPlaybackState;
  /** 当前正在加载 / 播放的消息 id；空闲与失败时为触发播放的那条消息 id。 */
  messageId: string | null;
  /** 失败态的结构化错误；非失败态为 null。 */
  issue: VoiceIssue | null;
  currentTime: number;
  duration: number | null;
}

export interface TtsPlayerOptions {
  /** TTS 适配器；为 null 时 `play()` 直接给出 `config` 结构化错误。 */
  adapter: TtsAdapter | null;
  /** 音频元素工厂；默认 `() => new Audio()`。 */
  audioFactory?: AudioFactory;
  /** Object URL 适配器；默认走 `URL.createObjectURL`。 */
  objectUrl?: ObjectUrlAdapter;
  /** 状态变化回调。 */
  onChange?: (snapshot: TtsPlayerSnapshot) => void;
  /** 音频自然播放结束（非用户停止）时回调，用于语音对话模式的自动回路。 */
  onEnded?: () => void;
}

const IDLE: TtsPlayerSnapshot = { state: 'idle', messageId: null, issue: null, currentTime: 0, duration: null };

/** 默认音频工厂：真实浏览器用 `new Audio()`。 */
export function defaultAudioFactory(): HTMLAudioElement {
  return new Audio();
}

/** 默认 Object URL 适配器；缺失 `URL.createObjectURL` 时退化为空串（不抛错）。 */
export function defaultObjectUrl(): ObjectUrlAdapter {
  return {
    create: (blob) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : ''),
    revoke: (url) => {
      if (url !== '' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    },
  };
}

export class TtsPlayer {
  private readonly adapter: TtsAdapter | null;
  private readonly audioFactory: AudioFactory;
  private readonly objectUrl: ObjectUrlAdapter;
  private readonly onChange: ((snapshot: TtsPlayerSnapshot) => void) | undefined;
  private readonly onEnded: (() => void) | undefined;

  private snapshot: TtsPlayerSnapshot = { ...IDLE };
  private audio: HTMLAudioElement | null = null;
  private objectUrlValue = '';
  /** 每次 play / stop 自增，避免过期的异步结果覆盖新状态。 */
  private token = 0;

  constructor(options: TtsPlayerOptions) {
    this.adapter = options.adapter;
    this.audioFactory = options.audioFactory ?? defaultAudioFactory;
    this.objectUrl = options.objectUrl ?? defaultObjectUrl();
    this.onChange = options.onChange;
    this.onEnded = options.onEnded;
  }

  getSnapshot(): TtsPlayerSnapshot {
    return { ...this.snapshot };
  }

  /** 合成并播放一段语音；失败时进入 `error` 态并返回（不抛出）。 */
  async play(messageId: string, req: TtsRequest): Promise<void> {
    const token = ++this.token;
    this.releaseAudio();

    if (this.adapter === null) {
      this.set('error', messageId, createConfigIssue('tts'));
      return;
    }

    this.set('loading', messageId, null);

    let audio: Blob;
    try {
      const result = await this.adapter.speak({ ...req, messageId });
      audio = result.audio;
    } catch (error) {
      if (token !== this.token) return;
      this.set('error', messageId, toVoiceIssue(error, 'tts'));
      return;
    }

    if (token !== this.token) return;

    try {
      const url = this.objectUrl.create(audio);
      this.objectUrlValue = url;
      await this.startAudio(messageId, url, token);
    } catch (error) {
      if (token !== this.token) return;
      this.set('error', messageId, toVoiceIssue(error, 'tts'));
    }
  }

  /** Play a persisted voice message without synthesizing it again. */
  async playSource(messageId: string, source: string): Promise<void> {
    const token = ++this.token;
    this.releaseAudio();
    this.set('loading', messageId, null);
    try {
      await this.startAudio(messageId, source, token);
    } catch (error) {
      if (token !== this.token) return;
      this.set('error', messageId, toVoiceIssue(error, 'tts'));
    }
  }

  seek(seconds: number): void {
    if (!this.audio || !Number.isFinite(seconds) || this.snapshot.duration === null) return;
    this.audio.currentTime = Math.max(0, Math.min(seconds, this.snapshot.duration));
    this.updateProgress(this.audio.currentTime, this.snapshot.duration);
  }

  private async startAudio(messageId: string, url: string, token: number): Promise<void> {
    const element = this.audioFactory();
    this.audio = element;
    const progress = (): void => {
      if (token !== this.token) return;
      this.updateProgress(
        Number.isFinite(element.currentTime) ? element.currentTime : 0,
        Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null,
      );
    };
    element.addEventListener('loadedmetadata', progress);
    element.addEventListener('durationchange', progress);
    element.addEventListener('timeupdate', progress);
    element.src = url;
    element.addEventListener('ended', () => {
      if (token !== this.token) return;
      this.stop();
      this.onEnded?.();
    });
    element.addEventListener('error', () => {
      if (token !== this.token) return;
      this.set('error', messageId, {
        source: 'tts',
        code: 'playback',
        message: '音频播放失败',
      });
    });
    await element.play();

    if (token !== this.token) return;
    progress();
    this.set('playing', messageId, null);
  }

  /** 停止播放并回到空闲态。 */
  stop(): void {
    this.token++;
    this.releaseAudio();
    this.set('idle', null, null);
  }

  /** 失败态 → 空闲态（用于用户关闭错误提示）。 */
  dismiss(): void {
    if (this.snapshot.state === 'error') this.set('idle', null, null);
  }

  /** 释放资源（组件卸载时调用）。 */
  dispose(): void {
    this.stop();
  }

  private set(state: TtsPlaybackState, messageId: string | null, issue: VoiceIssue | null): void {
    if (state === 'error') { this.token++; this.releaseAudio(); }
    this.snapshot = { ...(state === 'loading' ? IDLE : this.snapshot), state, messageId, issue };
    if (state === 'idle') this.snapshot = { ...IDLE };
    this.onChange?.(this.getSnapshot());
  }

  private updateProgress(currentTime: number, duration: number | null): void {
    if (this.snapshot.currentTime === currentTime && this.snapshot.duration === duration) return;
    this.snapshot = { ...this.snapshot, currentTime, duration };
    this.onChange?.(this.getSnapshot());
  }

  private releaseAudio(): void {
    if (this.audio !== null) {
      try {
        this.audio.pause();
      } catch {
        // 释放阶段忽略 pause 异常
      }
      this.audio = null;
    }
    if (this.objectUrlValue !== '') {
      this.objectUrl.revoke(this.objectUrlValue);
      this.objectUrlValue = '';
    }
  }
}
